import { UPDATE_SUPERUSER_NOTIFICATION_SETTING } from "@lib/graphql/mutations";
import { SUPERUSER_AUDIT_QUERY } from "@lib/graphql/queries";
import { useMutation, useQuery } from "urql";

interface SuperuserAuditPanelProps {
  isAdmin: boolean;
  notifyAdminsOnSuperuserAction: boolean;
  onSettingChanged: () => void;
}

interface AuditEvent {
  id: string;
  actionType: string;
  targetResource: string | null;
  targetId: string | null;
  httpMethod: string | null;
  path: string | null;
  graphqlMutation: string | null;
  performedAt: string;
  metadata: Record<string, unknown>;
  superuser: { id: string; name: string | null; email: string };
}

export function SuperuserAuditPanel({
  isAdmin,
  notifyAdminsOnSuperuserAction,
  onSettingChanged,
}: SuperuserAuditPanelProps) {
  const [{ data, fetching, error }] = useQuery({
    query: SUPERUSER_AUDIT_QUERY,
    pause: !isAdmin,
  });
  const [, updateNotification] = useMutation(UPDATE_SUPERUSER_NOTIFICATION_SETTING);

  if (!isAdmin) {
    return (
      <div className="mt-6 rounded-md border border-border bg-card p-4 text-sm text-text-secondary">
        Only organisation admins can view the Rarion staff audit log.
      </div>
    );
  }

  if (error) {
    return (
      <div className="mt-6 rounded-md border border-red-300 bg-red-50 p-4 text-sm text-red-700 dark:border-red-700 dark:bg-red-900/20 dark:text-red-400">
        Could not load audit events: {error.message}
      </div>
    );
  }

  const events: AuditEvent[] = data?.superuserAuditEvents ?? [];

  const handleToggle = async () => {
    await updateNotification({ input: { enabled: !notifyAdminsOnSuperuserAction } });
    onSettingChanged();
  };

  return (
    <div className="mt-6 space-y-6">
      <section className="rounded-md border border-border bg-card p-4">
        <h3 className="text-base font-semibold text-text-primary">Notifications</h3>
        <p className="mt-1 text-sm text-text-secondary">
          Email organisation admins whenever a Rarion staff member acts in this organisation. Off by
          default — turn on if your team wants real-time visibility.
        </p>
        <label className="mt-3 flex items-center gap-2 text-sm text-text-primary">
          <input
            type="checkbox"
            checked={notifyAdminsOnSuperuserAction}
            onChange={handleToggle}
            className="h-4 w-4 rounded border-border"
          />
          Notify admins when Rarion staff acts in this organisation
        </label>
      </section>

      <section>
        <h3 className="text-base font-semibold text-text-primary">Recent activity</h3>
        <p className="mt-1 text-sm text-text-secondary">
          Every action a Rarion staff member takes in this organisation is recorded here.
        </p>

        {fetching && <p className="mt-3 text-sm text-text-faint">Loading…</p>}

        {!fetching && events.length === 0 && (
          <div className="mt-3 rounded-md border border-border bg-card p-4 text-sm text-text-secondary">
            No audit events yet.
          </div>
        )}

        {events.length > 0 && (
          <div className="mt-3 overflow-x-auto rounded-md border border-border bg-card">
            <table className="min-w-full text-sm">
              <thead className="bg-card-alt text-xs uppercase tracking-wider text-text-tertiary">
                <tr>
                  <th className="px-4 py-2 text-left font-medium">When</th>
                  <th className="px-4 py-2 text-left font-medium">Actor</th>
                  <th className="px-4 py-2 text-left font-medium">Action</th>
                  <th className="px-4 py-2 text-left font-medium">Target</th>
                  <th className="px-4 py-2 text-left font-medium">Where</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {events.map((event) => (
                  <tr key={event.id} className="hover:bg-card-hover">
                    <td className="px-4 py-2 whitespace-nowrap text-text-secondary">
                      {new Date(event.performedAt).toLocaleString()}
                    </td>
                    <td className="px-4 py-2 text-text-secondary">{event.superuser.email}</td>
                    <td className="px-4 py-2 text-text-secondary">{event.actionType}</td>
                    <td className="px-4 py-2 text-text-secondary">{event.targetResource ?? "—"}</td>
                    <td className="px-4 py-2 font-mono text-xs text-text-secondary">
                      {event.graphqlMutation
                        ? `gql:${event.graphqlMutation}`
                        : `${event.httpMethod ?? ""} ${event.path ?? ""}`}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
